import type { CheckInResponseAction, Collab, InStoreCheckInStage } from "@foodfluence/shared";

const SHOPIFY_ARRIVAL_STAGES: ReadonlySet<InStoreCheckInStage> = new Set<InStoreCheckInStage>([
  "shopifyArrivalInitial",
  "shopifyArrivalFollowup",
  "shopifyArrivalResolve",
]);

const PACKAGE_ACTIONS: ReadonlySet<CheckInResponseAction> = new Set<CheckInResponseAction>([
  "packageReceived",
  "packageNotYet",
  "packageProblem",
]);

/** A message from the Shopify "did our package arrive?" check-in. */
export const isShopifyArrivalStage = (stage?: InStoreCheckInStage): boolean =>
  Boolean(stage && SHOPIFY_ARRIVAL_STAGES.has(stage));

/** "Yes, got it!" / "Not yet" / "Something's wrong" — recorded by the arrival endpoint, not the pre-visit one. */
export const isPackageAction = (action: CheckInResponseAction): boolean => PACKAGE_ACTIONS.has(action);

/**
 * True when an arrival check-in's chips no longer apply: the package was marked received (by the
 * creator on the order page, or for them), or the check-in was already answered. Without this, a
 * creator who confirmed receipt elsewhere would still see "Did everything arrive okay?" chips that
 * the dashboard would only ignore.
 */
export const isShopifyArrivalCheckInClosed = (
  collab?: Pick<Collab, "status" | "shopifyArrivalCheckInStatus"> | null,
): boolean =>
  collab?.status !== "pendingShipment" ||
  (collab.shopifyArrivalCheckInStatus !== "sent" && collab.shopifyArrivalCheckInStatus !== "followedUp");

/**
 * Whether a check-in message (sent at `sentMs`) still shows its chips: only the latest check-in
 * does, and for most check-ins only until the creator writes after it. An arrival check-in ignores
 * that last rule and closes on the collab's live status instead (`isShopifyArrivalCheckInClosed`):
 * the tap's echo is posted before the answer is recorded, so if recording fails the chips must stay
 * up for a retry.
 */
export const checkInChipsStillOpen = (params: {
  stage?: InStoreCheckInStage;
  sentMs: number;
  latestCheckInSentMs: number;
  lastInfluencerSentMs: number;
}): boolean => {
  if (params.sentMs !== params.latestCheckInSentMs) return false;
  return isShopifyArrivalStage(params.stage) || params.sentMs >= params.lastInfluencerSentMs;
};
